/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useForm } from "react-hook-form";

import * as Yup from "yup";

import { Link, Stack, StackItem, Text } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import { bodyContainerStackStyles } from "Components/Common/SharedTableStyles.styles";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";

import IGrantAnnualExceptionSidePanelProps from "./IGrantAnnualExceptionSidePanelProps";

export const GrantAnnualExceptionSidePanel: React.FunctionComponent<
  IGrantAnnualExceptionSidePanelProps
> = (props: IGrantAnnualExceptionSidePanelProps) => {
  const { isOpen, updateStatus, updateError, onDismiss, onSave } = props;

  const title = "Grant Annual Exception";

  const disableActions = updateStatus === OperationStatus.Pending;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        justification: Yup.string().required("Justification is required").max(3900),
      })
    ),
  });

  const dismissPanel = (): void => {
    !!onDismiss && onDismiss();
    reset();
  };

  const save = () =>
    handleSubmit((formData) => {
      !!onSave && onSave(formData.justification);
    });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isOpen}
        title={title}
        saveButtonLabel="Save"
        error={updateError}
        disableActions={disableActions}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack styles={bodyContainerStackStyles} tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <Text>
              This function will exclude the user from the annual review, and they will renew in the
              program at the end of the Award Period. Please review
              <Link
                href="https://dev.azure.com/devrel/mvp/_wiki/wikis/MVP.wiki/3291/Renewal-for-MVPs-with-special-circumstances"
                target="_blank"
              >
                {" Renewal for MVPs with special circumstances "}
              </Link>
              to provide
            </Text>
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="justification"
              control={control}
              errors={errors}
              isRequired
              isMultiline
              hasTooltip
              labelDisplayName="Justification"
              characterCountLimit={3900}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
